
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface ModelAvailability {
  modelName: string;
  basic: boolean;
  starter: boolean;
  professional: boolean;
}

interface ModelComparisonTableProps {
  models: ModelAvailability[];
}

const ModelComparisonTable: React.FC<ModelComparisonTableProps> = ({ models }) => {
  const isMobile = useIsMobile();
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});
  
  // Group models by category (based on name prefixes)
  const groupedModels = React.useMemo(() => {
    const groups: Record<string, ModelAvailability[]> = {};
    
    models.forEach(model => {
      // Extract category from model name (e.g., "Claude", "Gemini", "GPT")
      const parts = model.modelName.split(' ');
      const category = parts[0];
      
      if (!groups[category]) {
        groups[category] = [];
      }
      
      groups[category].push(model);
    });
    
    return groups;
  }, [models]);
  
  // Initialize expanded state for each category
  React.useEffect(() => {
    const initialState: Record<string, boolean> = {};
    Object.keys(groupedModels).forEach((category, index) => {
      // Expand the first category by default
      initialState[category] = index === 0;
    });
    setExpandedCategories(initialState);
  }, [groupedModels]);
  
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  if (isMobile) {
    // Mobile view - accordion-style cards
    return (
      <div className="space-y-4 w-full max-w-md mx-auto">
        {Object.entries(groupedModels).map(([category, categoryModels]) => (
          <Card key={category} className="overflow-hidden border shadow-sm">
            <div 
              onClick={() => toggleCategory(category)}
              className="p-4 bg-muted/30 flex justify-between items-center cursor-pointer"
            >
              <h3 className="font-medium">{category} Models</h3>
              {expandedCategories[category] ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            
            {expandedCategories[category] && (
              <CardContent className="p-0">
                <div className="divide-y">
                  {categoryModels.map((model, index) => (
                    <div key={index} className="p-4">
                      <div className="font-medium text-sm mb-2">{model.modelName}</div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="flex flex-col items-center">
                          <div className="text-xs mb-1 text-muted-foreground">Basic</div>
                          {model.basic ? (
                            <Check className="h-5 w-5 text-krix-purple" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground/30" />
                          )}
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="text-xs mb-1 text-muted-foreground">Starter</div>
                          {model.starter ? (
                            <Check className="h-5 w-5 text-krix-purple" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground/30" />
                          )}
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="text-xs mb-1 text-muted-foreground">Pro</div>
                          {model.professional ? (
                            <Check className="h-5 w-5 text-krix-purple" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground/30" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    );
  }
  
  // Desktop view - table layout
  return (
    <div className="w-full max-w-5xl mx-auto overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%]">Model</TableHead>
            <TableHead className="text-center">Basic</TableHead>
            <TableHead className="text-center">Starter</TableHead>
            <TableHead className="text-center">Professional</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(groupedModels).map(([category, categoryModels]) => (
            <React.Fragment key={category}>
              <TableRow className="bg-muted/20">
                <TableCell colSpan={4} className="font-medium">{category} Models</TableCell>
              </TableRow>
              {categoryModels.map((model, index) => (
                <TableRow key={`${category}-${index}`}>
                  <TableCell className="font-medium text-sm">{model.modelName}</TableCell>
                  <TableCell className="text-center">
                    {model.basic ? (
                      <Check className="h-5 w-5 mx-auto text-krix-purple" />
                    ) : (
                      <X className="h-5 w-5 mx-auto text-muted-foreground/30" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {model.starter ? (
                      <Check className="h-5 w-5 mx-auto text-krix-purple" />
                    ) : (
                      <X className="h-5 w-5 mx-auto text-muted-foreground/30" />
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {model.professional ? (
                      <Check className="h-5 w-5 mx-auto text-krix-purple" />
                    ) : (
                      <X className="h-5 w-5 mx-auto text-muted-foreground/30" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ModelComparisonTable;
